import { useInfiniteQuery } from '@tanstack/react-query';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { cadeCaseParams, fetchCadeCases } from '@/services/cade';
import type { CadeCoverageRow, CadePartyIdentity } from '@/services/types';

const PAGE_SIZE = 50;

/**
 * CADE's cases, and the first CADE screen that renders records rather than
 * refusals.
 *
 * Two disclosures sit ABOVE the table rather than on a detail page, because both
 * change how a reader should read the rows they are about to click into:
 *
 *  - Coverage. Votes reach 28% of decisions and fines 20%, so a case page with
 *    neither is the corpus behaving normally, not the screen failing.
 *  - Party identity. The names are unresolved -- 3,209 party rows produce 3,209
 *    distinct canonical names, so nothing was merged. Someone who searches a
 *    company and gets nine cases must not read that as the company's complete
 *    history, because the same firm also sits under its own misspellings.
 */
export function CadeCasesPage() {
  const { t, i18n } = useTranslation(['cade', 'common']);
  // Seeded from the URL so a link that means "every case naming this party"
  // arrives ALREADY FILTERED, as `/anatel/decisions?q=` does. Without it the
  // link would open the whole corpus while looking like it had scoped the list.
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  // `?type=` seeds the case-type filter the same way `?q=` seeds the search, so
  // a case-type row on `/cade/overview` can link straight into the filtered list
  // instead of rendering as text beside a filter that accepts exactly its value.
  // Only the ADJUDICATED vocabulary is offered by that filter, which is why the
  // overview links its judged rows and not the registry ones.
  const [caseType, setCaseType] = useState(searchParams.get('type') ?? '');
  // `?outcome=` and `?year=` seed the two filters the backend has accepted all
  // along and the screen never offered: the outcome vocabulary was fetched on
  // every request and rendered nowhere, and the archive year became a filter
  // once the session folder gave the corpus one.
  const [outcome, setOutcome] = useState(searchParams.get('outcome') ?? '');
  const [year, setYear] = useState(searchParams.get('year') ?? '');
  // One CNAE section from CADE's published register. A case with no sector there
  // never matches, which the reach line under the filters states.
  const [sector, setSector] = useState(searchParams.get('sector') ?? '');
  // Fields coded from the votes (served values only). They reach the coded cases alone, which
  // the line under the filters states; `?conduct=`, `?uf=` and `?market=` seed them.
  const [codedConduct, setCodedConduct] = useState(searchParams.get('conduct') ?? '');
  const [codedUf, setCodedUf] = useState(searchParams.get('uf') ?? '');
  const [codedMarket, setCodedMarket] = useState(searchParams.get('market') ?? '');
  const [codedHarm, setCodedHarm] = useState(searchParams.get('harm') ?? '');
  const [codedRemedy, setCodedRemedy] = useState(searchParams.get('remedy') ?? '');
  const [codedSg, setCodedSg] = useState(searchParams.get('sg') ?? '');
  const [codedRate, setCodedRate] = useState(searchParams.get('rate') ?? '');
  const [codedTccMin, setCodedTccMin] = useState(searchParams.get('tccMin') ?? '');
  const [codedTccMax, setCodedTccMax] = useState(searchParams.get('tccMax') ?? '');
  // ONE object for the fetch and the export, so the CSV can never drop a filter the screen applied.
  const coded = {
    codedConduct: codedConduct || null,
    codedUf: codedUf || null,
    codedMarket: codedMarket.trim() || null,
    codedHarm: codedHarm || null,
    codedRemedy: codedRemedy || null,
    codedSg: codedSg || null,
    codedRate: codedRate.trim() || null,
    codedTccMin: amount(codedTccMin),
    codedTccMax: amount(codedTccMax),
  };
  // Defaults to the judged population. `cases` also holds a bulk registry whose
  // rows have no decision, no outcome and no rapporteur; showing them by default
  // fills the first page with rows that are empty in every column.
  const [onlyAdjudicated, setOnlyAdjudicated] = useState(true);
  // Processes whose SEI record holds a court decision: that a court decided something, not what.
  const [courtInSei, setCourtInSei] = useState(searchParams.get('court_in_sei') === 'true');

  // Batches appended as the reader scrolls, rather than pages swapped by arrows.
  // 464 cases is eight arrow-clicks and eight full-table repaints, and the reader
  // loses their place on every one of them. `useInfiniteQuery` keeps the earlier
  // rows mounted, so scrolling back up costs nothing and no request is repeated.
  //
  // NOT `keepPreviousData`: with pages accumulating there is no previous page to
  // hold, and the trap that setting guards against -- a stale total rendered as
  // if it were the new one -- cannot arise here, because changing any filter
  // changes the query key and drops every accumulated page at once.
  const {
    data: paged,
    isPending,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'cade',
      'cases',
      query,
      caseType,
      outcome,
      year,
      sector,
      coded,
      onlyAdjudicated,
      courtInSei,
    ],
    queryFn: ({ pageParam }) =>
      fetchCadeCases({
        query: query || null,
        caseType: caseType || null,
        outcomeCodes: outcome ? [outcome] : [],
        sessionYear: year ? Number(year) : null,
        sector: sector || null,
        ...coded,
        onlyAdjudicated,
        courtInSei,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    // Driven by the server's own total, never by "the last page came back
    // full" -- a result set that is an exact multiple of PAGE_SIZE would
    // otherwise leave a button promising rows that do not exist.
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  // Every panel above the table reads corpus-level figures that are identical on
  // each page, so they come from the first one. Only the ROWS accumulate.
  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];

  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'BRL' });

  // The export replays the screen's filters through the same helper the fetch
  // uses -- never a second, hand-assembled query string. Page bounds are omitted
  // on purpose: an export is the whole result set, not the page in view.
  const exportParams = cadeCaseParams({
    query: query || null,
    caseType: caseType || null,
    outcomeCodes: outcome ? [outcome] : [],
    sessionYear: year ? Number(year) : null,
    sector: sector || null,
    ...coded,
    onlyAdjudicated,
    courtInSei,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:cases.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('cade:cases.subtitle')}{' '}
          {/* The one figure a client asks first lives on the register page,
              on a different basis (Qlik decision year, not the ata folder);
              named as a link rather than copied here under this page's
              window. */}
          <Link to="/cade/activity#time-to-decision" className="text-primary underline underline-offset-4">
            {t('cade:cases.timeToDecision')}
          </Link>
        </p>
      </header>

      <ExplainerCard i18nKey="cade:cases.explainer" />

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/cade/export/cases?${exportParams.toString()}`} />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <label className="block min-w-[16rem] flex-1 space-y-1">
          <span className="text-xs font-medium text-foreground">{t('cade:cases.search')}</span>
          <input
            type="search"
            value={query}
            placeholder={t('cade:cases.searchPlaceholder')}
            // No offset to reset: the filters ARE the query key, so changing
            // one drops every accumulated batch and starts from the first.
            onChange={(event) => setQuery(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
        <label className="block min-w-[14rem] space-y-1">
          <span className="text-xs font-medium text-foreground">{t('cade:cases.caseType')}</span>
          <select
            value={caseType}
            onChange={(event) => setCaseType(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t('cade:cases.allCaseTypes')}</option>
            {(data?.case_types ?? []).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[12rem] space-y-1">
          <span className="text-xs font-medium text-foreground">{t('cade:cases.outcome')}</span>
          <select
            value={outcome}
            onChange={(event) => setOutcome(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t('cade:cases.allOutcomes')}</option>
            {(data?.outcome_codes ?? []).map((code) => (
              <option key={code} value={code}>
                {t(`cade:overview.outcomes.${code}`, { defaultValue: code })}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[8rem] space-y-1">
          <span className="text-xs font-medium text-foreground">{t('cade:cases.year')}</span>
          <select
            value={year}
            onChange={(event) => setYear(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t('cade:cases.allYears')}</option>
            {(data?.years ?? []).map((value) => (
              <option key={value} value={String(value)}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="block min-w-[12rem] space-y-1">
          <span className="text-xs font-medium text-foreground">{t('cade:cases.sector')}</span>
          <select
            value={sector}
            onChange={(event) => setSector(event.target.value)}
            className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="">{t('cade:cases.allSectors')}</option>
            {(data?.sectors ?? []).map((option) => (
              <option key={option.code} value={option.code}>
                {option.label} ({number.format(option.cases)})
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Filters on the fields coded from the votes, offered only where the codings are
          loaded. Their reach is stated beside them: a case outside the coded set is not coded,
          which says nothing about what its vote holds. */}
      {(data?.coded_reach ?? 0) > 0 ? (
        <div className="space-y-1" data-coded-filters>
          <div className="flex flex-wrap items-end gap-3">
            <label className="block min-w-[14rem] space-y-1">
              <span className="text-xs font-medium text-foreground">
                {t('cade:codings.filters.conduct')}
              </span>
              <select
                value={codedConduct}
                onChange={(event) => setCodedConduct(event.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
              >
                <option value="">{t('cade:codings.filters.any')}</option>
                {(data?.coded_conducts ?? []).map((code) => (
                  <option key={code} value={code}>
                    {t(`cade:codings.values.conduct_type.${code}`, { defaultValue: code })}
                  </option>
                ))}
              </select>
            </label>
            <label className="block min-w-[8rem] space-y-1">
              <span className="text-xs font-medium text-foreground">
                {t('cade:codings.filters.uf')}
              </span>
              <select
                value={codedUf}
                onChange={(event) => setCodedUf(event.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
              >
                <option value="">{t('cade:codings.filters.any')}</option>
                {(data?.coded_ufs ?? []).map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </select>
            </label>
            <label className="block min-w-[14rem] flex-1 space-y-1">
              <span className="text-xs font-medium text-foreground">
                {t('cade:codings.filters.market')}
              </span>
              <input
                type="search"
                value={codedMarket}
                placeholder={t('cade:codings.filters.marketPlaceholder')}
                onChange={(event) => setCodedMarket(event.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <CodedSelect
              label={t('cade:codings.filters.harm')}
              value={codedHarm}
              onChange={setCodedHarm}
              options={data?.coded_harms ?? []}
              vocabulary="theory_of_harm"
            />
            <CodedSelect
              label={t('cade:codings.filters.remedy')}
              value={codedRemedy}
              onChange={setCodedRemedy}
              options={data?.coded_remedies ?? []}
              vocabulary="remedy_type"
            />
            <CodedSelect
              label={t('cade:codings.filters.sg')}
              value={codedSg}
              onChange={setCodedSg}
              options={data?.coded_sg_positions ?? []}
              vocabulary="position"
            />
            <label className="block min-w-[8rem] space-y-1">
              <span className="text-xs font-medium text-foreground">
                {t('cade:codings.filters.rate')}
              </span>
              <input
                type="search"
                value={codedRate}
                placeholder={t('cade:codings.filters.ratePlaceholder')}
                onChange={(event) => setCodedRate(event.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
            <label className="block min-w-[9rem] space-y-1">
              <span className="text-xs font-medium text-foreground">
                {t('cade:codings.filters.tccMin')}
              </span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={codedTccMin}
                onChange={(event) => setCodedTccMin(event.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
            <label className="block min-w-[9rem] space-y-1">
              <span className="text-xs font-medium text-foreground">
                {t('cade:codings.filters.tccMax')}
              </span>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                value={codedTccMax}
                onChange={(event) => setCodedTccMax(event.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
          </div>
          <p className="text-xs text-muted-foreground" data-coded-reach>
            {t('cade:codings.filters.reach', { count: data?.coded_reach ?? 0 })}
          </p>
        </div>
      ) : null}

      {/* A sector filter silently drops every case the register lists no sector
          for, so the reach is stated beside it rather than discovered. The
          per-sector counts above do not add up: a case can carry several. */}
      {data?.sector_reach ? (
        <p className="text-xs text-muted-foreground" data-sector-reach>
          {t('cade:cases.sectorReach', {
            with: number.format(data.sector_reach.with_sector),
            listed: number.format(data.sector_reach.listed),
          })}
        </p>
      ) : null}

      {/* The population switch, beside the filters rather than buried in them:
          it changes what the corpus IS, not merely which rows pass. */}
      <label className="flex w-fit items-center gap-2 text-xs text-foreground">
        <input
          type="checkbox"
          checked={onlyAdjudicated}
          onChange={(event) => setOnlyAdjudicated(event.target.checked)}
          className="h-4 w-4 rounded border"
        />
        {t('cade:cases.onlyAdjudicated')}
      </label>
      {data?.court_in_sei_available || courtInSei ? (
        <label className="flex w-fit items-center gap-2 text-xs text-foreground">
          <input
            type="checkbox"
            checked={courtInSei}
            onChange={(event) => setCourtInSei(event.target.checked)}
            className="h-4 w-4 rounded border"
            data-court-in-sei
          />
          {t('cade:cases.courtInSei')}
        </label>
      ) : null}
      {courtInSei ? (
        <p className="text-xs text-muted-foreground" data-court-in-sei-note>
          {t('cade:cases.courtInSeiNote')}
        </p>
      ) : null}

      {/* Shown only while the registry is included, because that is the state a
          reader will otherwise mistake for a broken table. */}
      {!onlyAdjudicated ? (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-foreground">
          {t('cade:cases.registryNote')}
        </p>
      ) : null}

      {data ? (
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('cade:cases.registry.title')}
          </h2>
          <Answer answer={data.registry} help={t('cade:cases.registry.help')}>
            {(registry) => (
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <dt className="text-muted-foreground">{t('cade:cases.registry.total')}</dt>
                  <dd className="tabular-nums text-foreground">
                    {number.format(registry.cases)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('cade:cases.registry.adjudicated')}</dt>
                  <dd className="tabular-nums text-foreground">
                    {number.format(registry.adjudicated)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('cade:cases.registry.registryOnly')}</dt>
                  <dd className="tabular-nums text-foreground">
                    {number.format(registry.registry_only)}
                  </dd>
                </div>
              </dl>
            )}
          </Answer>
          <div className="mt-3">
            <FieldGuide i18nKey="cade:cases.guide.registry" />
          </div>
        </section>
      ) : null}

      {data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('cade:cases.coverage.title')}
            </h2>
            <Answer answer={data.coverage} help={t('cade:cases.coverage.help')}>
              {(rows) => <CoverageList rows={rows} />}
            </Answer>
          </section>

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              {t('cade:cases.parties.title')}
            </h2>
            <Answer answer={data.party_identity} help={t('cade:cases.parties.help')}>
              {(identity) => <PartyIdentityPanel identity={identity} />}
            </Answer>
          </section>
        </div>
      ) : null}

      {data ? <FieldGuide i18nKey="cade:cases.guide.panels" /> : null}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !data ? (
        <SkeletonLoader rows={8} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('cade:cases.emptyHint')} />
      ) : (
        <div className="space-y-3">
          {/* The live region. Page-level prose elsewhere quotes these same
              numbers, so an assertion scoped to the whole page can resolve to a
              collapsed explainer instead of the real count. */}
          {/* The live region, and it now announces a GROWING count rather than a
              window. `data-loaded` and `data-total` carry the raw integers, so a
              test reads the numbers instead of re-deriving the locale's
              separators and matching on a formatted string. */}
          <p
            className="text-xs text-muted-foreground"
            aria-live="polite"
            data-loaded={items.length}
            data-total={data.total}
          >
            {t('cade:cases.loaded', {
              loaded: number.format(items.length),
              total: number.format(data.total),
            })}
          </p>

          {/* Bounded height with its OWN scrollbar. Batches accumulate, so an
              unbounded table grows the page until the filters, the disclosures
              and the export button are all several screens above the rows --
              and the browser scrollbar stops meaning anything, because its
              thumb shrinks every time a batch lands. Capping the container
              keeps the page a fixed shape and puts the growth inside it.

              The header is sticky for the same reason: six columns of case
              numbers, types and outcomes are unreadable once the labels have
              scrolled away. */}
          {/* A labelled region, and deliberately NOT `tabIndex={0}`.
              `SidebarLayout`'s `<main>` needs that, with an eslint disable, because
              an analytical page can contain nothing focusable at all and axe then
              reports `scrollable-region-focusable`. This container is the opposite
              case: every row carries a case-number link and the batch control sits
              inside it, so keyboard scrolling already works and a tab stop on the
              box itself would only add one the reader has to pass through. */}
          <div
            ref={scrollRef}
            className="max-h-[65vh] overflow-auto rounded-lg border"
            role="region"
            aria-label={t('cade:cases.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('cade:cases.columns.case')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:cases.columns.type')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:cases.columns.sectors')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:cases.columns.parties')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:cases.columns.relator')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:cases.columns.outcome')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:cases.columns.sessions')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:cases.columns.fine')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.case_key} className="border-t align-top hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <Link
                        to={`/cade/c/${item.case_key}`}
                        className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
                      >
                        {item.case_number}
                      </Link>
                      <span className="block text-xs text-muted-foreground">
                        {t('cade:cases.decisionCount', { count: item.decisions })}
                      </span>
                    </td>
                    <td className="max-w-[12rem] px-3 py-2 text-muted-foreground">
                      {item.case_type ?? '—'}
                    </td>
                    <td className="max-w-xs px-3 py-2 text-foreground">
                      {item.party_sample.length === 0 ? (
                        '—'
                      ) : (
                        <>
                          <span className="line-clamp-2">{item.party_sample.join('; ')}</span>
                          {item.party_count > item.party_sample.length ? (
                            <span className="block text-xs text-muted-foreground">
                              {t('cade:cases.moreParties', {
                                count: item.party_count - item.party_sample.length,
                              })}
                            </span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="max-w-[14rem] px-3 py-2 text-muted-foreground">
                      {item.sectors.join(' · ')}
                    </td>
                    <td className="max-w-[12rem] px-3 py-2 text-muted-foreground">
                      {item.relators.length === 0
                        ? '—'
                        : item.relators.map((relator, index) => (
                            // `relator_slugs` is aligned index for index with
                            // `relators`: both aggregate from one subquery ordered
                            // by the name, so they cannot drift apart.
                            <span key={relator}>
                              {index > 0 ? '; ' : ''}
                              {item.relator_slugs[index] ? (
                                <Link
                                  to={`/cade/r/${item.relator_slugs[index]}`}
                                  className="text-primary underline-offset-4 hover:underline"
                                >
                                  {relator}
                                </Link>
                              ) : (
                                relator
                              )}
                            </span>
                          ))}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {item.outcome_codes.length === 0
                        ? t('cade:overview.outcomes.UNCLASSIFIED', { defaultValue: 'Unclassified' })
                        : item.outcome_codes
                            .map((code) =>
                              t(`cade:overview.outcomes.${code}`, { defaultValue: code }),
                            )
                            .join(' · ')}
                    </td>
                    {/* The archive years of the case's sessions, one year or a
                        span. Blank for a registry-only row, which has no
                        session at all. */}
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                      {item.first_session_year === null || item.last_session_year === null
                        ? ''
                        : item.first_session_year === item.last_session_year
                          ? String(item.first_session_year)
                          : t('common:asOf.yearWindow', {
                              first: item.first_session_year,
                              last: item.last_session_year,
                            })}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-foreground">
                      {/* Blank, never 0: a zero in a money column is a value, and
                          four fifths of decisions record no fine at all. */}
                      {item.fine_total_brl === null ? '' : currency.format(item.fine_total_brl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* The sentinel lives INSIDE the scroll container, because that is
                what scrolls now. Left outside it, it sits below a fixed-height
                box and enters the viewport only when the whole PAGE is scrolled
                -- which, with the table capped, may never happen. */}
            <div ref={sentinelRef} aria-hidden className="h-px" />

            <div className="flex justify-center py-3">
              {hasNextPage ? (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isFetchingNextPage}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {isFetchingNextPage
                    ? t('cade:cases.loadingMore')
                    : t('cade:cases.loadMore', { count: PAGE_SIZE })}
                </button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('cade:cases.allLoaded', { total: number.format(data.total) })}
                </p>
              )}
            </div>
          </div>

          <FieldGuide i18nKey="cade:cases.guide.columns" />
          {/* The Type column renders the tribunal's own Portuguese procedure
              label verbatim. "Requerimento de TCC" and "Embargos de Declaracao"
              are not guessable from the string, so the vocabulary is defined
              here rather than left to the reader. Shared with the overview,
              which renders the same labels in its case-type table. */}
          <FieldGuide i18nKey="cade:vocabulary.caseTypes" />

        </div>
      )}
    </div>
  );
}

function CoverageList({ rows }: { rows: CadeCoverageRow[] }) {
  const { t, i18n } = useTranslation(['cade']);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.attachment} className="space-y-1">
          <div className="flex items-baseline justify-between gap-3 text-xs">
            <span className="text-foreground">
              {t(`cade:cases.coverage.attachments.${row.attachment}`, {
                defaultValue: row.attachment,
              })}
            </span>
            <span className="shrink-0 tabular-nums text-muted-foreground">
              {number.format(row.decisions)} / {number.format(row.total)} ·{' '}
              {percent.format(row.share)}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded bg-muted">
            <div
              className="h-full rounded bg-foreground/60"
              style={{ width: `${Math.round(row.share * 100)}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Party identity as a stated limitation.
 *
 * `merged` is the number the panel exists for: the canonicalisation collapsed
 * zero rows, so "canonical name" here means "a copy of the raw name", not a
 * resolved company.
 */
function PartyIdentityPanel({ identity }: { identity: CadePartyIdentity }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-2 text-xs">
      <dl className="grid grid-cols-3 gap-2">
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.records')}</dt>
          <dd className="tabular-nums text-foreground">{number.format(identity.parties)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.distinctNames')}</dt>
          <dd className="tabular-nums text-foreground">{number.format(identity.distinct_names)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('cade:cases.parties.withCnpj')}</dt>
          <dd className="tabular-nums text-foreground">{number.format(identity.with_cnpj)}</dd>
        </div>
      </dl>
      {identity.cnpj_cluster_conflicts ? (
        <p className="text-muted-foreground">
          {t('cade:cases.parties.cnpjConflicts', { count: identity.cnpj_cluster_conflicts })}
        </p>
      ) : null}
      <p className="rounded border border-amber-500/40 bg-amber-500/5 p-2 text-foreground">
        {identity.resolved
          ? t('cade:cases.parties.resolved', { count: identity.merged })
          : t('cade:cases.parties.unresolved')}
      </p>
      {identity.artefact_names > 0 ? (
        <p className="text-muted-foreground">
          {t('cade:cases.parties.artefactNames', { count: identity.artefact_names })}
        </p>
      ) : null}
    </div>
  );
}

/** A non-negative number typed in a box, or null when blank or not a number. */
function amount(raw: string): number | null {
  const value = Number(raw);
  return raw.trim() !== '' && Number.isFinite(value) && value >= 0 ? value : null;
}

/** One filter over a coded vocabulary: values come from the data, labels from the locale. */
function CodedSelect({
  label,
  value,
  onChange,
  options,
  vocabulary,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  vocabulary: string;
}) {
  const { t } = useTranslation(['cade']);
  return (
    <label className="block min-w-[12rem] space-y-1">
      <span className="text-xs font-medium text-foreground">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
      >
        <option value="">{t('cade:codings.filters.any')}</option>
        {options.map((code) => (
          <option key={code} value={code}>
            {t(`cade:codings.values.${vocabulary}.${code}`, { defaultValue: code })}
          </option>
        ))}
      </select>
    </label>
  );
}
